import { useState, useEffect, useCallback, useRef } from 'react';
import useFocusPulse from '../../hooks/useFocusPulse';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  Sparkles, Loader2, Plus, Trash2, Save, CheckCircle2, AlertTriangle,
  XCircle, ShieldCheck, Award, Users, ChevronDown, ChevronUp, Bot, Check, X, Download,
  Paperclip, FileCheck2, RotateCcw
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { describeApiError } from '../../lib/apiErrors';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  MET: { label: 'Met', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500' },
  PARTIAL: { label: 'Partial', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
  GAP: { label: 'Gap', icon: XCircle, color: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
};

const SEVERITY_CONFIG = {
  HIGH: { label: 'High', color: 'bg-red-100 text-red-700' },
  MEDIUM: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  LOW: { label: 'Low', color: 'bg-blue-100 text-blue-600' },
};

function SummaryBar({ summary }) {
  const { total_lcats = 0, met = 0, partial = 0, gap = 0, compliance_score = 0, high_severity_gaps = 0 } = summary || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="matrix-summary">
      <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
        <p className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{total_lcats}</p>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider">Total LCATs</p>
      </div>
      <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center">
        <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{met}</p>
        <p className="text-[11px] text-emerald-600 uppercase tracking-wider">Met</p>
      </div>
      <div className="bg-amber-50 rounded-lg border border-amber-200 p-3 text-center">
        <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{partial}</p>
        <p className="text-[11px] text-amber-600 uppercase tracking-wider">Partial</p>
      </div>
      <div className="bg-red-50 rounded-lg border border-red-200 p-3 text-center">
        <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{gap}</p>
        <p className="text-[11px] text-red-600 uppercase tracking-wider">Gaps</p>
      </div>
      <div className={cn('rounded-lg border p-3 text-center', compliance_score >= 70 ? 'bg-emerald-50 border-emerald-200' : compliance_score >= 40 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200')}>
        <p className={cn('text-2xl font-bold', compliance_score >= 70 ? 'text-emerald-700' : compliance_score >= 40 ? 'text-amber-700' : 'text-red-700')} style={{ fontFamily: 'Manrope, sans-serif' }}>{compliance_score}%</p>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider">Compliance</p>
      </div>
    </div>
  );
}

function EntryRow({ entry, onUpdate, onDelete }) {
  const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.GAP;
  const severityCfg = SEVERITY_CONFIG[entry.gap_severity] || SEVERITY_CONFIG.MEDIUM;
  const StatusIcon = statusCfg.icon;

  return (
    <div
      data-testid={`matrix-entry-${entry.id}`}
      className={cn(
        'grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-slate-100 transition-colors',
        entry.status === 'GAP' && entry.gap_severity === 'HIGH' ? 'bg-red-50/50' : 'hover:bg-slate-50/50'
      )}
    >
      {/* LCAT Name + Code + Task Areas (multi-value) */}
      <div className="col-span-3">
        <p className="text-sm font-medium text-slate-800">{entry.lcat_name}</p>
        <div className="flex flex-wrap items-center gap-1 mt-0.5">
          {entry.lcat_code && <span className="text-[10px] text-slate-400 font-mono">{entry.lcat_code}</span>}
          {/* v3.11.11 — Phase C: render every Task Area in the multi-value list.
              Backwards-compat: if `task_areas` is missing, fall back to the
              single-value `task_area`. */}
          {(() => {
            const areas = (Array.isArray(entry.task_areas) && entry.task_areas.length > 0)
              ? entry.task_areas
              : (entry.task_area ? [entry.task_area] : []);
            return areas.map((ta) => (
              <Badge
                key={ta}
                className="text-[9px] bg-cyan-50 text-cyan-700 border-cyan-200 px-1.5 py-0 font-normal"
                title="Federal Task Area"
              >
                {ta}
              </Badge>
            ));
          })()}
        </div>
        {entry.notes && (
          <p className="text-[11px] text-slate-500 mt-0.5 leading-tight line-clamp-2">{entry.notes}</p>
        )}
      </div>

      {/* Qty */}
      <div className="col-span-1 text-center">
        <div className="flex items-center gap-1 justify-center">
          <Input
            data-testid={`entry-available-${entry.id}`}
            type="number"
            min="0"
            value={entry.available_qty}
            onChange={(e) => onUpdate(entry.id, { available_qty: parseInt(e.target.value) || 0 })}
            className="w-12 h-7 text-xs text-center p-0"
          />
          <span className="text-xs text-slate-400">/</span>
          <span className="text-xs font-semibold text-slate-700">{entry.required_qty}</span>
        </div>
      </div>

      {/* Clearance */}
      <div className="col-span-1 text-center">
        {entry.required_clearance ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger>
                <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">
                  <ShieldCheck className="w-3 h-3 mr-0.5" />{entry.required_clearance}
                </Badge>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Required: {entry.required_clearance}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : <span className="text-xs text-slate-300">None</span>}
      </div>

      {/* Certifications */}
      <div className="col-span-2">
        <div className="flex flex-wrap gap-1">
          {(entry.required_certifications || []).slice(0, 3).map((cert, i) => (
            <Badge key={i} className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200 px-1.5 py-0">
              <Award className="w-2.5 h-2.5 mr-0.5" />{cert}
            </Badge>
          ))}
          {(entry.required_certifications || []).length > 3 && (
            <Badge className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0">+{entry.required_certifications.length - 3}</Badge>
          )}
          {(!entry.required_certifications || entry.required_certifications.length === 0) && (
            <span className="text-xs text-slate-300">None</span>
          )}
        </div>
      </div>

      {/* Experience */}
      <div className="col-span-1 text-center">
        {entry.required_experience_years ? (
          <span className="text-xs font-medium text-slate-700">{entry.required_experience_years} yrs</span>
        ) : <span className="text-xs text-slate-300">-</span>}
      </div>

      {/* Status */}
      <div className="col-span-2">
        <Select value={entry.status} onValueChange={(val) => onUpdate(entry.id, { status: val })}>
          <SelectTrigger className="h-7 text-xs w-full" data-testid={`entry-status-${entry.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-1.5">
                  <div className={cn('w-2 h-2 rounded-full', cfg.dot)} />
                  <span>{cfg.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={entry.gap_severity} onValueChange={(val) => onUpdate(entry.id, { gap_severity: val })}>
          <SelectTrigger className="h-6 text-[10px] w-full mt-1 border-dashed" data-testid={`entry-severity-${entry.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Teaming */}
      <div className="col-span-1 text-center">
        {entry.teaming_recommendation ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger>
                <Badge className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 cursor-help">
                  <Users className="w-2.5 h-2.5 mr-0.5" />Team
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[250px]"><p className="text-xs">{entry.teaming_recommendation}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>

      {/* Actions */}
      <div className="col-span-1 text-right">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => onDelete(entry.id)} data-testid={`entry-delete-${entry.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function CapabilityMatrix({ pursuitId, token }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDetected, setAiDetected] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [groupByTaskArea, setGroupByTaskArea] = useState(false);
  // v3.20 — pricing template state
  const [templateInfo, setTemplateInfo] = useState(null); // { filename, count, lcats }
  const [templateUploading, setTemplateUploading] = useState(false);
  const templateFileRef = useRef(null);

  const [newEntry, setNewEntry] = useState({
    lcat_name: '', lcat_code: '', task_area: '', required_qty: 1, available_qty: 0,
    required_clearance: '', required_certifications: '', required_experience_years: '',
    status: 'GAP', gap_severity: 'MEDIUM', notes: '',
  });

  // v3.12.3 — Coverage Map deep-link pulse (shared hook).
  useFocusPulse({
    rows: entries,
    getSelector: (id) => `[data-testid="matrix-entry-${id}"]`,
  });

  // Load
  useEffect(() => {
    if (!pursuitId || !token) return;
    fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setEntries(data.entries || []);
        setSummary(data.summary || {});
        if (data.updated_at) setLastSaved(data.updated_at);
        // v3.20 — restore template info if already uploaded
        if (data.lcat_template?.lcats?.length) setTemplateInfo(data.lcat_template);
      })
      .catch(() => {});
  }, [pursuitId, token]);

  // Save all. `silent=true` skips the toast — used by auto-save after
  // AI batches finish so we don't double-toast on top of the AI banner.
  const handleSave = async (silent = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        setLastSaved(new Date().toISOString());
        if (!silent) toast.success('Capability Matrix saved');
      } else if (!silent) toast.error('Save failed');
    } catch { if (!silent) toast.error('Network error'); }
    setSaving(false);
  };

  // Auto-save trigger: persist whenever entries change AND no batch
  // is mid-flight. Debounced 800ms. pWin Feedback Loop reads from
  // db.capability_matrix, so without this the AI-detected LCATs are
  // invisible to the Go/No-Go composite score.
  const autosaveTimerRef = useRef(null);
  useEffect(() => {
    if (entries.length === 0) return;
    const anyPending = aiDetected?.batch_status?.includes('pending');
    if (anyPending) return;
    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      handleSave(true);
    }, 800);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, aiDetected?.batch_status]);

  // Download PDF
  const [downloading, setDownloading] = useState(false);
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Capability_Matrix.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else toast.error('PDF generation failed');
    } catch { toast.error('Network error'); }
    setDownloading(false);
  };

  // v3.20 — Upload pricing template to extract authoritative LCAT list
  const handleUploadTemplate = async (file) => {
    if (!file) return;
    setTemplateUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix/upload-lcat-template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setTemplateInfo({ filename: data.filename, count: data.count, lcats: data.lcats });
        toast.success(`Template loaded — ${data.count} LCATs found in "${data.filename}"`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || 'Template upload failed');
      }
    } catch {
      toast.error('Network error uploading template');
    }
    setTemplateUploading(false);
    // Reset file input so same file can be re-uploaded
    if (templateFileRef.current) templateFileRef.current.value = '';
  };

  // Update entry locally
  const handleUpdateEntry = useCallback((entryId, updates) => {
    setEntries(prev => {
      const updated = prev.map(e => e.id === entryId ? { ...e, ...updates } : e);
      // Recompute summary locally
      const total = updated.length;
      const met = updated.filter(e => e.status === 'MET').length;
      const partial = updated.filter(e => e.status === 'PARTIAL').length;
      const gap = updated.filter(e => e.status === 'GAP').length;
      setSummary({ total_lcats: total, met, partial, gap, compliance_score: total > 0 ? Math.round((met / total) * 100) : 0, high_severity_gaps: updated.filter(e => e.status === 'GAP' && e.gap_severity === 'HIGH').length });
      return updated;
    });
  }, []);

  // Delete entry locally
  const handleDeleteEntry = useCallback((entryId) => {
    setEntries(prev => {
      const updated = prev.filter(e => e.id !== entryId);
      const total = updated.length;
      const met = updated.filter(e => e.status === 'MET').length;
      setSummary({ total_lcats: total, met, partial: updated.filter(e => e.status === 'PARTIAL').length, gap: updated.filter(e => e.status === 'GAP').length, compliance_score: total > 0 ? Math.round((met / total) * 100) : 0, high_severity_gaps: updated.filter(e => e.status === 'GAP' && e.gap_severity === 'HIGH').length });
      return updated;
    });
  }, []);

  // Add entry from modal
  const handleAddEntry = () => {
    if (!newEntry.lcat_name.trim()) { toast.error('LCAT name required'); return; }
    // v3.11.11 — coerce comma-separated Task Area input → list of strings.
    // Backend accepts both `task_area` (single) and `task_areas` (list);
    // we send both so back-compat readers stay happy.
    const taskAreasList = (newEntry.task_area || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const entry = {
      id: crypto.randomUUID(),
      ...newEntry,
      task_area: taskAreasList[0] || '',
      task_areas: taskAreasList,
      required_qty: parseInt(newEntry.required_qty) || 1,
      available_qty: parseInt(newEntry.available_qty) || 0,
      required_experience_years: newEntry.required_experience_years ? parseInt(newEntry.required_experience_years) : null,
      required_certifications: newEntry.required_certifications ? newEntry.required_certifications.split(',').map(s => s.trim()).filter(Boolean) : [],
      lcat_source: 'manual',  // v3.20 — mark as manually added so it survives re-detect
    };
    setEntries(prev => [...prev, entry]);
    setAddModalOpen(false);
    setNewEntry({ lcat_name: '', lcat_code: '', task_area: '', required_qty: 1, available_qty: 0, required_clearance: '', required_certifications: '', required_experience_years: '', status: 'GAP', gap_severity: 'MEDIUM', notes: '' });
    toast.success('LCAT added');
  };

  // AI Detect (v3.20): two-phase progressive flow.
  // KEY FIXES:
  //   1. On re-run, clears previous AI-detected entries (lcat_source === 'ai_detected'
  //      or 'template') so the list doesn't keep growing. Manually-added entries
  //      (lcat_source === 'manual' or no source) are always preserved.
  //   2. Passes template_lcats to each batch when a pricing template is on file,
  //      so AI only returns LCATs from the government's authoritative list.
  //   3. Marks every AI-returned entry with lcat_source so the next run can
  //      replace them.
  const handleAiDetect = async () => {
    setAiLoading(true);
    try {
      // ── Phase 0: replace-on-rerun ────────────────────────────────
      // Clear entries that were produced by AI in a previous run so
      // re-running doesn't grow the list. Keep manually-added ones.
      const manualEntries = entries.filter(
        e => e.lcat_source !== 'ai_detected' && e.lcat_source !== 'template'
      );
      setEntries(manualEntries);

      // ── Phase 1: instant init ────────────────────────────────────
      const initRes = await fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix/ai-detect/init`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        if (initRes.status === 412) {
          toast.error(describeApiError(err, 'Run AI Shred first'), { duration: 8000 });
        } else {
          toast.error(describeApiError(err, 'AI detection failed'));
        }
        setAiLoading(false);
        return;
      }
      const initData = await initRes.json();

      // Sync template info from init response (in case it was just uploaded)
      const templateLcats = initData.template?.lcats || null;
      if (initData.has_template && initData.template && !templateInfo) {
        setTemplateInfo(initData.template);
      }

      const batchStatus = (initData.batch_plan || []).map(() => 'pending');
      setAiDetected({
        batch_plan: initData.batch_plan || [],
        batch_status: batchStatus,
        requirements_total: initData.requirements_total || 0,
        model_used: '',
        template_lcats: templateLcats,       // v3.20 — for retry
        has_template: initData.has_template || false,
      });

      const modeLabel = initData.has_template
        ? `template (${templateLcats?.length} LCATs)`
        : 'AI extraction';
      toast.success(
        `Scanning ${initData.requirements_total} requirements in ${initData.batch_plan.length} batch${initData.batch_plan.length === 1 ? '' : 'es'} · ${modeLabel}…`
      );

      // Seed dedup set from manual entries only
      const seedSeen = new Set(
        manualEntries.map(e => String(e.lcat_name || '').trim().toLowerCase())
      );
      const seenAcrossBatches = new Set(seedSeen);

      const detectBatch = async (batchIdx, requirementIds) => {
        try {
          const body = { requirement_ids: requirementIds };
          if (templateLcats?.length) body.template_lcats = templateLcats;
          const res = await fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix/ai-detect/batch`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { ok: false, batchIdx, requirementIds, error: err };
          }
          const data = await res.json();
          return { ok: true, batchIdx, requirementIds, data };
        } catch {
          return { ok: false, batchIdx, requirementIds, error: { detail: 'Network error' } };
        }
      };

      const apply = (result) => {
        if (result.ok) {
          // Dedup across batches; backend already marks lcat_source
          const additions = (result.data.entries || []).filter(e => {
            const k = String(e.lcat_name || '').trim().toLowerCase();
            if (!k || seenAcrossBatches.has(k)) return false;
            seenAcrossBatches.add(k);
            return true;
          });
          if (additions.length > 0) {
            setEntries(prev => {
              const next = [...prev, ...additions];
              const total = next.length;
              const met = next.filter(e => e.status === 'MET').length;
              const partial = next.filter(e => e.status === 'PARTIAL').length;
              const gap = next.filter(e => e.status === 'GAP').length;
              setSummary({
                total_lcats: total, met, partial, gap,
                compliance_score: total > 0 ? Math.round((met / total) * 100) : 0,
                high_severity_gaps: next.filter(e => e.status === 'GAP' && e.gap_severity === 'HIGH').length,
              });
              return next;
            });
          }
          setAiDetected(prev => {
            if (!prev) return prev;
            const nextStatus = [...(prev.batch_status || [])];
            nextStatus[result.batchIdx] = 'ok';
            return {
              ...prev,
              batch_status: nextStatus,
              model_used: result.data.model_used || prev.model_used,
            };
          });
        } else {
          setAiDetected(prev => {
            if (!prev) return prev;
            const nextStatus = [...(prev.batch_status || [])];
            nextStatus[result.batchIdx] = 'failed';
            return { ...prev, batch_status: nextStatus };
          });
        }
      };

      const PARALLEL = 3;
      const queue = (initData.batch_plan || []).map((ids, idx) => ({ idx, ids }));
      const runWorker = async () => {
        while (queue.length > 0) {
          const job = queue.shift();
          if (!job) break;
          const result = await detectBatch(job.idx, job.ids);
          apply(result);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(PARALLEL, (initData.batch_plan || []).length) }, () => runWorker())
      );
    } catch {
      toast.error('Network error');
    }
    setAiLoading(false);
  };

  // Per-batch retry (v3.10.1). Re-runs ONE batch and merges new (deduped)
  // LCATs into the main table.
  const retryBatch = async (batchIdx) => {
    if (!aiDetected || !aiDetected.batch_plan) return;
    const requirementIds = aiDetected.batch_plan[batchIdx];
    if (!requirementIds || requirementIds.length === 0) return;
    setAiDetected(prev => {
      if (!prev) return prev;
      const nextStatus = [...(prev.batch_status || [])];
      nextStatus[batchIdx] = 'pending';
      return { ...prev, batch_status: nextStatus };
    });
    try {
      const retryBody = { requirement_ids: requirementIds };
      if (aiDetected?.template_lcats?.length) retryBody.template_lcats = aiDetected.template_lcats;
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/capability-matrix/ai-detect/batch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(retryBody),
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(prev => {
          const seen = new Set(
            prev.map(e => String(e.lcat_name || '').trim().toLowerCase())
          );
          const additions = (data.entries || []).filter(e => {
            const k = String(e.lcat_name || '').trim().toLowerCase();
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
          });
          const next = [...prev, ...additions];
          const total = next.length;
          const met = next.filter(e => e.status === 'MET').length;
          const partial = next.filter(e => e.status === 'PARTIAL').length;
          const gap = next.filter(e => e.status === 'GAP').length;
          setSummary({
            total_lcats: total, met, partial, gap,
            compliance_score: total > 0 ? Math.round((met / total) * 100) : 0,
            high_severity_gaps: next.filter(e => e.status === 'GAP' && e.gap_severity === 'HIGH').length,
          });
          return next;
        });
        setAiDetected(prev => {
          if (!prev) return prev;
          const nextStatus = [...(prev.batch_status || [])];
          nextStatus[batchIdx] = 'ok';
          return { ...prev, batch_status: nextStatus, model_used: data.model_used || prev.model_used };
        });
        toast.success(`Batch ${batchIdx + 1} processed`);
      } else {
        setAiDetected(prev => {
          if (!prev) return prev;
          const nextStatus = [...(prev.batch_status || [])];
          nextStatus[batchIdx] = 'failed';
          return { ...prev, batch_status: nextStatus };
        });
        const err = await res.json().catch(() => ({}));
        toast.error(describeApiError(err, 'Batch retry failed'));
      }
    } catch {
      toast.error('Network error');
    }
  };

  const handleDismissAi = () => { setAiDetected(null); };

  return (
    <Card className="border-slate-300 shadow-md" data-testid="capability-matrix">
      {/* Header */}
      <CardHeader
        className="cursor-pointer border-b border-slate-200 bg-slate-900 rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-cyan-400" />
            <CardTitle className="text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              LCAT Map
            </CardTitle>
            <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-xs">
              {entries.length} LCATs
            </Badge>
            {(summary.gap || 0) > 0 && (
              <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                <XCircle className="w-3 h-3 mr-1" />{summary.gap} Gap{summary.gap > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {entries.length > 0 && (
              <span className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary.compliance_score || 0}%
              </span>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          {/* Hidden template file input */}
          <input
            ref={templateFileRef}
            type="file"
            accept=".xlsx,.xls,.csv,.docx,.pdf,.txt"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadTemplate(f); }}
          />

          {/* AI Detect Bar */}
          {!aiDetected && (
            <div className="border-b border-cyan-200" data-testid="ai-detect-bar">
              {/* Template row */}
              <div className="flex items-center justify-between px-5 py-2 bg-cyan-50/70">
                <div className="flex items-center gap-2">
                  <FileCheck2 className="w-3.5 h-3.5 text-cyan-600" />
                  <span className="text-xs font-medium text-cyan-800">Pricing Template</span>
                  {templateInfo ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 text-[11px] font-medium">
                      <Check className="w-3 h-3" />
                      {templateInfo.filename} · {templateInfo.count} LCATs
                    </span>
                  ) : (
                    <span className="text-xs text-cyan-500 italic">
                      Upload the Government's pricing template for exact LCAT matching
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={templateUploading}
                  onClick={(e) => { e.stopPropagation(); templateFileRef.current?.click(); }}
                  className="border-cyan-300 text-cyan-700 hover:bg-cyan-100 text-xs gap-1.5 h-7"
                  data-testid="upload-template-btn"
                >
                  {templateUploading
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Paperclip className="w-3 h-3" />}
                  {templateUploading ? 'Parsing…' : templateInfo ? 'Re-upload' : 'Upload Template'}
                </Button>
              </div>
              {/* Detect row */}
              <div className="flex items-center justify-between px-5 py-2.5 bg-gradient-to-r from-cyan-50 to-indigo-50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-cyan-500" />
                  <span className="text-sm font-medium text-cyan-800">AI LCAT Detection</span>
                  <span className="text-xs text-cyan-600">
                    {templateInfo
                      ? `Will match against ${templateInfo.count} LCATs from template — no guessing`
                      : 'Extracts explicitly-named LCATs from extracted requirements'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {entries.some(e => e.lcat_source === 'ai_detected' || e.lcat_source === 'template') && (
                    <span className="text-[10px] text-amber-600 italic">re-run replaces previous AI results</span>
                  )}
                  <Button
                    data-testid="ai-detect-btn"
                    size="sm" disabled={aiLoading}
                    onClick={(e) => { e.stopPropagation(); handleAiDetect(); }}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs gap-1.5"
                  >
                    {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {aiLoading ? 'Detecting…' : templateInfo ? 'Detect from Template' : 'Detect LCATs'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* AI Results Banner — progress strip only (LCATs land in the
              MAIN table, not duplicated here). */}
          {aiDetected && (
            <div className="px-5 py-3 bg-gradient-to-r from-cyan-100 to-indigo-100 border-b border-cyan-300" data-testid="ai-detect-results">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-cyan-600" />
                  <span className="text-sm font-semibold text-cyan-800">
                    {aiDetected.batch_status && aiDetected.batch_status.includes('pending')
                      ? `Detecting LCATs… ${entries.length} found so far`
                      : aiDetected.batch_status && aiDetected.batch_status.includes('failed')
                        ? 'Some batches failed — click Retry on each to re-run'
                        : `LCAT detection complete · ${entries.length} in table`}
                  </span>
                  {aiDetected.has_template && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                      <FileCheck2 className="w-3 h-3 mr-0.5" />template-matched
                    </Badge>
                  )}
                  {aiDetected.model_used && <Badge className="bg-cyan-200 text-cyan-700 text-[10px]">{aiDetected.model_used}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  {!aiDetected.batch_status?.includes('pending') && (
                    <Button
                      data-testid="ai-redetect-btn"
                      size="sm" variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleDismissAi(); handleAiDetect(); }}
                      disabled={aiLoading}
                      className="text-cyan-600 hover:text-cyan-800 text-xs gap-1"
                    >
                      <RotateCcw className="w-3 h-3" /> Re-run
                    </Button>
                  )}
                  <Button data-testid="ai-dismiss-lcats-btn" size="sm" variant="ghost" onClick={handleDismissAi} className="text-slate-500 hover:text-red-500 text-xs gap-1">
                    <X className="w-3 h-3" /> Dismiss
                  </Button>
                </div>
              </div>
              {/* v3.10.0: Per-batch progress strip — one chip per batch. */}
              {aiDetected.batch_status?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="cm-batch-progress">
                  <span className="text-[10px] font-semibold text-cyan-700 uppercase mr-1">Batches:</span>
                  {aiDetected.batch_status.map((status, idx) => (
                    <span
                      key={idx}
                      data-testid={`cm-batch-${idx}-${status}`}
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                        status === 'ok' && 'bg-emerald-100 text-emerald-700',
                        status === 'pending' && 'bg-cyan-100 text-cyan-700 animate-pulse',
                        status === 'failed' && 'bg-red-100 text-red-700 border border-red-300',
                      )}
                    >
                      Batch {idx + 1}
                      {status === 'failed' && (
                        <button
                          type="button"
                          onClick={() => retryBatch(idx)}
                          className="underline ml-1 hover:text-red-900"
                          data-testid={`cm-batch-${idx}-retry`}
                        >
                          retry
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="px-5 pt-4">
            <SummaryBar summary={summary} />
          </div>

          {/* Column Headers */}
          <div className="grid grid-cols-12 gap-3 items-center px-4 py-2 bg-slate-100 border-y border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-3 flex items-center justify-between">
              <span>Labor Category</span>
              {/* v3.11.5: always rendered. Disabled + tooltip when no task
                  areas exist yet (instead of hidden) for discoverability. */}
              {(() => {
                const hasTaskAreas = entries.some(e =>
                  (Array.isArray(e.task_areas) && e.task_areas.length > 0) || e.task_area
                );
                const btn = (
                  <button
                    type="button"
                    data-testid="cm-group-by-task-area"
                    onClick={() => setGroupByTaskArea(v => !v)}
                    disabled={!hasTaskAreas}
                    className={cn(
                      'text-[9px] font-medium normal-case px-1.5 py-0.5 rounded border transition-colors',
                      !hasTaskAreas
                        ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                        : groupByTaskArea
                          ? 'bg-cyan-500 text-white border-cyan-600'
                          : 'bg-white text-cyan-700 border-cyan-200 hover:bg-cyan-50'
                    )}
                    title={hasTaskAreas ? 'Group LCATs by Task Area' : undefined}
                  >
                    {groupByTaskArea && hasTaskAreas ? 'Grouped' : 'Group by Area'}
                  </button>
                );
                if (hasTaskAreas) return btn;
                return (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex" data-testid="cm-group-by-task-area-wrap">{btn}</span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs max-w-[240px]">
                        No Task Areas on any LCAT yet. Set Task Area on an entry (or run AI Detect) to enable grouping.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })()}
            </div>
            <div className="col-span-1 text-center">Avail / Req</div>
            <div className="col-span-1 text-center">Clearance</div>
            <div className="col-span-2">Certifications</div>
            <div className="col-span-1 text-center">Exp</div>
            <div className="col-span-2">Status / Severity</div>
            <div className="col-span-1 text-center">Teaming</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {/* Entries */}
          {entries.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-400">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium">No LCATs defined yet</p>
              <p className="text-xs mt-1">Use AI Detection to extract from RFP, or add manually</p>
            </div>
          ) : groupByTaskArea ? (
            <div data-testid="matrix-entries-grouped">
              {(() => {
                // Group entries by task_area, preserving insertion order.
                // v3.11.11 — multi-value: LCATs with 2+ Task Areas appear in
                // EACH of their groups (this matches the customer template's
                // intent: an Architect spanning Cloud + SW Dev should be
                // counted under both columns).
                const groups = new Map();
                for (const e of entries) {
                  const areas = (Array.isArray(e.task_areas) && e.task_areas.length > 0)
                    ? e.task_areas
                    : (e.task_area && e.task_area.trim() ? [e.task_area.trim()] : ['__unassigned__']);
                  for (const ta of areas) {
                    const key = ta || '__unassigned__';
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key).push(e);
                  }
                }
                const orderedKeys = Array.from(groups.keys()).sort((a, b) => {
                  if (a === '__unassigned__') return 1;
                  if (b === '__unassigned__') return -1;
                  return a.localeCompare(b);
                });
                return orderedKeys.map(key => (
                  <div key={key} data-testid={`cm-group-${key}`}>
                    <div className="px-4 py-2 bg-cyan-50/60 border-y border-cyan-100 text-[11px] font-semibold uppercase tracking-wider text-cyan-700 flex items-center justify-between">
                      <span>{key === '__unassigned__' ? 'Task Area: Unassigned' : key}</span>
                      <span className="text-[10px] font-normal text-cyan-600 normal-case">{groups.get(key).length} LCAT{groups.get(key).length === 1 ? '' : 's'}</span>
                    </div>
                    {groups.get(key).map(entry => (
                      <EntryRow key={entry.id} entry={entry} onUpdate={handleUpdateEntry} onDelete={handleDeleteEntry} />
                    ))}
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div data-testid="matrix-entries-list">
              {entries.map(entry => (
                <EntryRow key={entry.id} entry={entry} onUpdate={handleUpdateEntry} onDelete={handleDeleteEntry} />
              ))}
            </div>
          )}

          {/* Actions Footer */}
          <div className="px-5 py-4 bg-white border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button data-testid="matrix-save-btn" onClick={handleSave} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save Matrix'}
                </Button>
                <Button data-testid="matrix-pdf-btn" variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading || entries.length === 0}>
                  <Download className="w-4 h-4 mr-1" />{downloading ? 'Generating...' : 'Download PDF'}
                </Button>
                <Button data-testid="matrix-add-btn" variant="outline" size="sm" onClick={() => setAddModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />Add LCAT
                </Button>
              </div>
              {lastSaved && <p className="text-xs text-slate-400">Last saved: {new Date(lastSaved).toLocaleString()}</p>}
            </div>
          </div>
        </CardContent>
      )}

      {/* Add LCAT Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="max-w-md" data-testid="add-lcat-modal">
          <DialogHeader>
            <DialogTitle className="text-lg">Add Labor Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">LCAT Name *</label>
              <Input data-testid="add-lcat-name" value={newEntry.lcat_name} onChange={e => setNewEntry(p => ({ ...p, lcat_name: e.target.value }))} placeholder="e.g., Senior Systems Engineer" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Task Areas</label>
              <Input
                data-testid="add-lcat-task-area"
                value={newEntry.task_area}
                onChange={e => setNewEntry(p => ({ ...p, task_area: e.target.value }))}
                placeholder="e.g., Analytics & Data Engineering, Cloud Operations"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">v3.11.11 — multi-value supported. Separate multiple Task Areas with commas (e.g. "Cloud Operations, Software Development").</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Code</label>
                <Input data-testid="add-lcat-code" value={newEntry.lcat_code} onChange={e => setNewEntry(p => ({ ...p, lcat_code: e.target.value }))} placeholder="SSE-01" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Required Qty</label>
                <Input data-testid="add-lcat-qty" type="number" min="1" value={newEntry.required_qty} onChange={e => setNewEntry(p => ({ ...p, required_qty: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Clearance</label>
                <Input value={newEntry.required_clearance} onChange={e => setNewEntry(p => ({ ...p, required_clearance: e.target.value }))} placeholder="TS/SCI" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Experience (years)</label>
                <Input type="number" min="0" value={newEntry.required_experience_years} onChange={e => setNewEntry(p => ({ ...p, required_experience_years: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Certifications (comma-separated)</label>
              <Input value={newEntry.required_certifications} onChange={e => setNewEntry(p => ({ ...p, required_certifications: e.target.value }))} placeholder="PMP, CISSP, AWS SA" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Status</label>
                <Select value={newEntry.status} onValueChange={val => setNewEntry(p => ({ ...p, status: val }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GAP">Gap</SelectItem>
                    <SelectItem value="PARTIAL">Partial</SelectItem>
                    <SelectItem value="MET">Met</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Severity</label>
                <Select value={newEntry.gap_severity} onValueChange={val => setNewEntry(p => ({ ...p, gap_severity: val }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Notes</label>
              <Textarea value={newEntry.notes} onChange={e => setNewEntry(p => ({ ...p, notes: e.target.value }))} placeholder="Reference section, key personnel requirement, etc." className="min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>Cancel</Button>
            <Button data-testid="add-lcat-submit" onClick={handleAddEntry}>Add LCAT</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
